import { useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePathname, useSearchParams } from 'next/navigation';
import { requestNewGnbInfo } from '@apis/renewal/gnb';
import useGlobalState from '@hooks/useGlobalState';
import { getSsrDeviceValue } from '@utils/ssrDeviceUtil';
import type { NewGnbInfo } from '@wconcept/wconcept-common/models/renewal/gnb';

export function useGaGnbInfo() {
  const { initialData, assistData, deviceInfo, domainGender, appInfo } =
    useGlobalState<{
      readonly gnbInfo?: NewGnbInfo[];
    }>();
  const pathname = usePathname();
  const search = useSearchParams().toString();
  const queryClient = useQueryClient();
  const isMobile =
    getSsrDeviceValue(
      deviceInfo?.userAgent ??
        (typeof window !== 'undefined' ? window.navigator.userAgent : '')
    ) !== 'PC';
  const identity = JSON.stringify([
    appInfo?.isNativeAppBoth,
    assistData.isLogin,
    assistData.user.userNo,
    assistData.user.grade,
    domainGender,
    isMobile,
  ]);
  const suppliedGnb = useRef({ data: initialData?.gnbInfo, identity });
  if (suppliedGnb.current.data !== initialData?.gnbInfo) {
    suppliedGnb.current = { data: initialData?.gnbInfo, identity };
  }

  const options = useMemo(
    () => ({
      queryKey: ['gaGnbInfo', identity, pathname, search],
      queryFn: async () => {
        const data = await requestNewGnbInfo();
        return isMobile
          ? [
              ...(data?.mobileMenu?.windowN?.map(item => ({
                ...item,
                window: 'N' as const,
              })) ?? []),
              ...(data?.mobileMenu?.windowY?.map(item => ({
                ...item,
                window: 'Y' as const,
              })) ?? []),
            ]
          : // eslint-disable-next-line prettier/prettier
            data?.pcMenu ?? [];
      },
      staleTime: Infinity,
      gcTime: 0,
    }),
    [identity, pathname, search, isMobile]
  );

  const currentOptions = useRef({ options, data: initialData?.gnbInfo });
  currentOptions.current = { options, data: initialData?.gnbInfo };

  // Keep the shared result only while this page's GA consumers are mounted.
  useQuery({ ...options, enabled: false });

  return useCallback(
    async (refresh = false) => {
      const data =
        !refresh &&
        suppliedGnb.current.identity === identity &&
        initialData?.gnbInfo !== undefined
          ? initialData.gnbInfo
          : await queryClient.fetchQuery({
              ...options,
              staleTime: refresh ? 0 : options.staleTime,
            });
      return currentOptions.current.options === options &&
        currentOptions.current.data === initialData?.gnbInfo
        ? data
        : undefined;
    },
    [identity, initialData?.gnbInfo, options, queryClient]
  );
}
